import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Button,
  Typography,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
} from "@mui/material";
import ModalPopup from "../../components/ModalPopup";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { api } from "../../utils/api";
import { COLORS } from "../../utils/colors";
import { getCurrentP1Region } from "../../utils/p1Region";
import { SmsStatusDisplay } from "../Customer/SmsStatusDisplay";
const {
  formatCommunicationsDateTime,
  normalizeSmsProvider,
} = require("../Customer/smsProviderDisplay");

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

function formatEventKey(value) {
  if (!value) return "Unknown";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const TYPE_GROUPS = [
  { id: "operational", label: "Operational" },
  { id: "payment_failure", label: "Payment failure" },
  { id: "marketing", label: "Marketing" },
];

const PAYMENT_FAILURE_KEYS = new Set([
  "initial_subscription_reminder",
  "subscription_reminder_payment_issue",
  "subscription_final_reminder_before_suspension",
  "subscription_reminder_final_notice",
  "p1_admin_failed_payment_subscription",
]);

const MARKETING_KEYS = new Set([
  "P1 - ALL STATES AND TERRITORIES #1",
  "Priority One - QLD, WA, VIC & NSW #2",
  "Priority One - QLD, WA, VIC, NSW & ACT #1",
  "p1_car_wash_vouchers",
  "p1_early_may_2_follow_up",
  "p1_early_may_follow_up",
  "p1_june_broadway_offer",
  "p1_june_end_of_financial_year",
  "p1_may_retain_customers",
  "p1_retain_customer_simple_jan_25",
  "p1_unlimited_wash_offer_simple_march_25",
  "p1_unlimited_wash_offer_simple_march_25_retarget",
]);

function communicationGroup(key) {
  if (PAYMENT_FAILURE_KEYS.has(key)) return "payment_failure";
  if (MARKETING_KEYS.has(key)) return "marketing";
  const normalized = String(key || "").toLowerCase();
  if (
    /fail|past_due|payment_issue|suspension|final_notice|subscription_reminder/.test(
      normalized
    )
  ) {
    return "payment_failure";
  }
  if (
    /offer|retain|follow_up|voucher|retarget|campaign|marketing|financial_year/.test(
      normalized
    )
  ) {
    return "marketing";
  }
  return "operational";
}

function eventKeyQuery(selection, keys) {
  if (!selection || selection === "all") return "all";
  if (String(selection).startsWith("group:")) {
    const groupId = selection.slice("group:".length);
    const matched = keys.filter((key) => communicationGroup(key) === groupId);
    return matched.length ? matched.join(",") : "__none__";
  }
  return selection;
}

function typeFilterLabel(selection) {
  if (!selection || selection === "all") return "All types";
  if (String(selection).startsWith("group:")) {
    const group = TYPE_GROUPS.find((item) => item.id === selection.slice("group:".length));
    return group ? group.label : "All types";
  }
  return formatEventKey(selection);
}

function recipientName(recipient) {
  if (!recipient) return "—";
  const name = `${recipient.first || ""} ${recipient.last || ""}`.trim();
  return name || recipient.email || recipient.phone || "—";
}

function ProviderChip({ provider }) {
  const label = normalizeSmsProvider(provider);
  return (
    <Chip
      size="small"
      label={label}
      variant="outlined"
      color={provider === "ClickSend" ? "primary" : "default"}
    />
  );
}

function StatusChip({ status }) {
  const normalized = String(status || "Sent");
  const lower = normalized.toLowerCase();
  const isDelivered = lower === "delivered";
  const isFailed = lower === "failed";
  return (
    <Chip
      size="small"
      label={isDelivered ? "Delivered" : isFailed ? "Failed" : normalized}
      color={isDelivered ? "success" : isFailed ? "error" : "default"}
      variant={isDelivered ? "filled" : "outlined"}
    />
  );
}

const emptyPagination = { page: 0, pageSize: 25, total: 0, totalPages: 0 };

const Communications = () => {
  const p1Region = getCurrentP1Region();
  const formatSentDate = (value) =>
    formatCommunicationsDateTime(value, p1Region);

  const [commTab, setCommTab] = useState(0);
  const [types, setTypes] = useState({ email: [], sms: [] });
  const [eventKey, setEventKey] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [emailLoading, setEmailLoading] = useState(true);
  const [smsLoading, setSmsLoading] = useState(true);
  const [emailError, setEmailError] = useState("");
  const [smsError, setSmsError] = useState("");
  const [emails, setEmails] = useState([]);
  const [sms, setSms] = useState([]);
  const [emailPagination, setEmailPagination] = useState(emptyPagination);
  const [smsPagination, setSmsPagination] = useState(emptyPagination);
  const [emailPage, setEmailPage] = useState(0);
  const [smsPage, setSmsPage] = useState(0);
  const [emailRowsPerPage, setEmailRowsPerPage] = useState(25);
  const [smsRowsPerPage, setSmsRowsPerPage] = useState(25);

  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDetailLoading, setEmailDetailLoading] = useState(false);
  const [emailDetailError, setEmailDetailError] = useState("");
  const searchReady = useRef(false);

  useEffect(() => {
    if (!searchReady.current) {
      searchReady.current = true;
      return undefined;
    }
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim());
      setEmailPage(0);
      setSmsPage(0);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api("v1/communications/types", "GET");
        if (!cancelled && response.data?.success) {
          setTypes({
            email: response.data.data?.email || [],
            sms: response.data.data?.sms || [],
          });
        }
      } catch (err) {
        console.error("Error loading communication types:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const typeOptions = useMemo(
    () => [...new Set([...types.email, ...types.sms])].sort(),
    [types]
  );

  const loadEmails = useCallback(async () => {
    setEmailLoading(true);
    setEmailError("");
    try {
      const params = new URLSearchParams({
        channel: "email",
        page: String(emailPage + 1),
        pageSize: String(emailRowsPerPage),
        eventKey: eventKeyQuery(eventKey, typeOptions),
      });
      if (search) params.set("q", search);
      const response = await api(`v1/communications?${params.toString()}`, "GET");
      if (response.data?.success) {
        const data = response.data.data || {};
        setEmails(data.records || []);
        const p = data.pagination || {};
        setEmailPagination({
          page: Math.max((p.page || 1) - 1, 0),
          pageSize: p.pageSize || emailRowsPerPage,
          total: p.total ?? 0,
          totalPages: p.totalPages ?? 0,
        });
      } else {
        setEmailError(response.data?.message || "Failed to load emails.");
      }
    } catch (err) {
      setEmailError(err.response?.data?.message || "Unable to load emails.");
    } finally {
      setEmailLoading(false);
    }
  }, [emailPage, emailRowsPerPage, eventKey, search, typeOptions]);

  const loadSms = useCallback(async () => {
    setSmsLoading(true);
    setSmsError("");
    try {
      const params = new URLSearchParams({
        channel: "sms",
        page: String(smsPage + 1),
        pageSize: String(smsRowsPerPage),
        eventKey: eventKeyQuery(eventKey, typeOptions),
      });
      if (search) params.set("q", search);
      const response = await api(`v1/communications?${params.toString()}`, "GET");
      if (response.data?.success) {
        const data = response.data.data || {};
        const rows = data.sms || data.records || [];
        setSms(rows);
        const p = data.pagination?.sms || data.pagination || {};
        setSmsPagination({
          page: Math.max((p.page || 1) - 1, 0),
          pageSize: p.pageSize || smsRowsPerPage,
          total: p.total ?? 0,
          totalPages: p.totalPages ?? 0,
        });
      } else {
        setSmsError(response.data?.message || "Failed to load SMS.");
      }
    } catch (err) {
      setSmsError(err.response?.data?.message || "Unable to load SMS.");
    } finally {
      setSmsLoading(false);
    }
  }, [smsPage, smsRowsPerPage, eventKey, search, typeOptions]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  useEffect(() => {
    loadSms();
  }, [loadSms]);

  const handleViewEmail = async (row) => {
    setSelectedEmail({ ...row, html: null });
    setEmailDetailError("");
    setEmailDetailLoading(true);
    try {
      const response = await api(`v1/communications/email/${row.id}`, "GET");
      if (response.data?.success) {
        setSelectedEmail(response.data.data);
      } else {
        setEmailDetailError(response.data?.message || "Failed to load email content.");
      }
    } catch (err) {
      setEmailDetailError(err.response?.data?.message || "Unable to load email content.");
    } finally {
      setEmailDetailLoading(false);
    }
  };

  return (
    <div className="page communications flex flex-column items-center p-4 w-full">
      <div className="pageTitle flex flex-col w-full text-center lg:flex-row lg:text-left lg:justify-between mb-4 gap-3">
        <div>
          <h2>Communications</h2>
          <Typography variant="body2" color="text.secondary">
            Emails and SMS sent through Hustle Outbound in the last 29 days.
          </Typography>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <TextField
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            placeholder="Search name, email, or phone"
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel id="comms-type-filter">Type</InputLabel>
            <Select
              labelId="comms-type-filter"
              label="Type"
              value={eventKey}
              renderValue={(value) => typeFilterLabel(value)}
              onChange={(e) => {
                setEventKey(e.target.value);
                setEmailPage(0);
                setSmsPage(0);
              }}
            >
              <MenuItem value="all">All types</MenuItem>
              {TYPE_GROUPS.map((group) => {
                const keys = typeOptions.filter(
                  (key) => communicationGroup(key) === group.id
                );
                if (!keys.length) return null;
                return [
                  <ListSubheader key={`${group.id}-header`} disableSticky>
                    {group.label}
                  </ListSubheader>,
                  <MenuItem key={`group:${group.id}`} value={`group:${group.id}`}>
                    All {group.label.toLowerCase()}
                  </MenuItem>,
                  ...keys.map((key) => (
                    <MenuItem key={key} value={key} sx={{ pl: 4 }}>
                      {formatEventKey(key)}
                    </MenuItem>
                  )),
                ];
              })}
            </Select>
          </FormControl>
        </div>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}>
        <Tabs
          value={commTab}
          onChange={(e, value) => setCommTab(value)}
          aria-label="communications tabs"
        >
          <Tab label={`Emails (${emailPagination.total})`} />
          <Tab label={`SMS (${smsPagination.total})`} />
        </Tabs>
      </Box>

      <div className="w-full mt-4">
        {emailLoading && commTab === 0 && <GlobalLoadingSpinner />}
        {smsLoading && commTab === 1 && <GlobalLoadingSpinner />}

        {!emailLoading && emailError && commTab === 0 && (
          <Typography color="error" variant="body2">
            {emailError}
          </Typography>
        )}
        {!smsLoading && smsError && commTab === 1 && (
          <Typography color="error" variant="body2">
            {smsError}
          </Typography>
        )}

        {commTab === 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 180 }}>Sent</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Recipient</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 110 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!emailLoading && emails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No emails match this filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  emails.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatSentDate(row.sentDate)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{recipientName(row.recipient)}</span>
                          <span className="text-xs text-gray-500">
                            {row.recipient?.email || "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{formatEventKey(row.eventKey)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 items-start">
                          <span className="text-sm">{row.subject || "Email"}</span>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewEmail(row)}
                            sx={{
                              borderColor: COLORS.priorityBlue.hex,
                              color: COLORS.priorityBlue.hex,
                            }}
                          >
                            View email
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={row.status} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={emailPagination.total}
              page={emailPage}
              onPageChange={(e, newPage) => setEmailPage(newPage)}
              rowsPerPage={emailRowsPerPage}
              onRowsPerPageChange={(e) => {
                setEmailRowsPerPage(parseInt(e.target.value, 10));
                setEmailPage(0);
              }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          </TableContainer>
        )}

        {commTab === 1 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 180 }}>Sent</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Recipient</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 110 }}>Provider</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 120 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!smsLoading && sms.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No SMS match this filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sms.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatSentDate(row.sentDate)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{recipientName(row.recipient)}</span>
                          <span className="text-xs text-gray-500">
                            {row.recipient?.phone || row.recipient?.email || "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{formatEventKey(row.eventKey)}</TableCell>
                      <TableCell sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {row.message}
                      </TableCell>
                      <TableCell>
                        <ProviderChip provider={row.provider || "HOB"} />
                      </TableCell>
                      <TableCell>
                        <SmsStatusDisplay
                          status={row.status}
                          statusDetail={row.statusDetail}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={smsPagination.total}
              page={smsPage}
              onPageChange={(e, newPage) => setSmsPage(newPage)}
              rowsPerPage={smsRowsPerPage}
              onRowsPerPageChange={(e) => {
                setSmsRowsPerPage(parseInt(e.target.value, 10));
                setSmsPage(0);
              }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          </TableContainer>
        )}
      </div>

      <ModalPopup
        showModal={!!selectedEmail}
        handleShowModal={() => {
          setSelectedEmail(null);
          setEmailDetailError("");
          setEmailDetailLoading(false);
        }}
        popupTitle={selectedEmail?.subject || "Email"}
        popupContent={
          selectedEmail ? (
            <div className="flex flex-col gap-3 max-h-[70vh] overflow-auto">
              <Typography variant="body2" color="text.secondary">
                {formatEventKey(selectedEmail.eventKey)} · Sent{" "}
                {formatSentDate(selectedEmail.sentDate)}
                {selectedEmail.recipient?.email
                  ? ` · ${selectedEmail.recipient.email}`
                  : ""}
              </Typography>
              {emailDetailLoading && (
                <div className="flex justify-center py-8">
                  <CircularProgress size={32} />
                </div>
              )}
              {emailDetailError && (
                <Typography color="error" variant="body2">
                  {emailDetailError}
                </Typography>
              )}
              {!emailDetailLoading && !emailDetailError && selectedEmail.html && (
                <iframe
                  title={selectedEmail.subject || "Email"}
                  sandbox="allow-popups allow-popups-to-escape-sandbox"
                  srcDoc={selectedEmail.html}
                  className="w-full border rounded bg-white"
                  style={{ height: "70vh" }}
                />
              )}
            </div>
          ) : null
        }
        cancelButtonText="Close"
        confirmButtonText=""
        confirmCallbackAction=""
      />
    </div>
  );
};

export default Communications;
